// ==================== WHITEPAPER ACCESS MODAL ====================
// Lead form shown before a whitepaper can be downloaded or read. On success the
// API returns a short-lived token and the PDF is opened from the API.
// Give it a new `key` each time it opens so the form and result reset.

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { User, Mail, Phone, Building, Download, ExternalLink, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Modal, ModalBody } from '../ui/Modal';
import { FormInput } from '../ui/FormInput';
import { Button } from '../ui/Button';
import { ROUTES } from '../../constants';
import { whitepapersService, type WhitepaperAccessAction } from '../../services';
import {
  whitepaperAccessFormSchema,
  normalizePhone,
  type WhitepaperAccessFormData,
} from '../../utils/form-validations';

interface WhitepaperAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  whitepaperId: string;
  whitepaperTitle: string;
  action: WhitepaperAccessAction;
}

const EMPTY_FORM: WhitepaperAccessFormData = { fullName: '', email: '', phone: '', company: '', website: '' };

const WhitepaperAccessModal = ({
  isOpen,
  onClose,
  whitepaperId,
  whitepaperTitle,
  action,
}: WhitepaperAccessModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState(0);
  const isDownload = action === 'download';

  // Parent remounts this component (via key) on every open, so the form starts empty
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WhitepaperAccessFormData>({
    resolver: zodResolver(whitepaperAccessFormSchema),
    defaultValues: EMPTY_FORM,
  });

  const onSubmit = async (data: WhitepaperAccessFormData) => {
    setIsSubmitting(true);
    setFormError(null);

    const response = await whitepapersService.requestAccess(whitepaperId, {
      fullName: data.fullName,
      email: data.email,
      phone: normalizePhone(data.phone),
      company: data.company || undefined,
      action,
      website: data.website || undefined,
    });

    setIsSubmitting(false);

    if (!response.success || !response.data?.token) {
      const msg = response.message || 'Something went wrong. Please try again later.';
      setFormError(
        msg.includes('Too Many Requests')
          ? 'Too many requests. Please wait a few minutes and try again.'
          : msg,
      );
      return;
    }

    const url = whitepapersService.getFileUrl(whitepaperId, response.data.token);
    setFileUrl(url);
    setExpiresAt(new Date(response.data.expiresAt).getTime());

    if (isDownload) {
      // The API answers with Content-Disposition: attachment, so the page stays put
      window.location.assign(url);
    } else {
      // May be blocked as a popup after the async request — the success panel has a link too
      window.open(url, '_blank', 'noopener');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={isDownload ? 'Download the whitepaper' : 'Read the whitepaper'}
      subtitle={whitepaperTitle}
    >
      <ModalBody>
        {fileUrl ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-4"
            aria-live="polite"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-(--color-success)/15 mb-4">
              <CheckCircle2 size={28} className="text-(--color-success)" />
            </div>
            <p className="text-lg font-semibold text-white mb-2">
              {isDownload ? 'Your download has started' : 'Your whitepaper is ready'}
            </p>
            <p className="text-sm text-(--color-soft-gray) mb-6">
              {isDownload
                ? "If it didn't start, use the button below. The link is valid for 10 minutes."
                : 'It opened in a new tab. If not, use the button below. The link is valid for 10 minutes.'}
            </p>
            <a
              href={fileUrl}
              {...(isDownload ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
              onClick={(e) => {
                // Never send the visitor to an expired link — back to an empty form instead
                if (Date.now() >= expiresAt) {
                  e.preventDefault();
                  reset(EMPTY_FORM);
                  setFileUrl(null);
                  setFormError('That link has expired. Please submit the form again to get a new one.');
                }
              }}
              className="btn btn-primary"
            >
              {isDownload ? <Download size={18} /> : <ExternalLink size={18} />}
              {isDownload ? 'Download PDF' : 'Open Whitepaper'}
            </a>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <p className="text-sm text-(--color-soft-gray)">
              Tell us a little about yourself to {isDownload ? 'download' : 'read'} this whitepaper.
            </p>

            <FormInput
              label="Full Name"
              placeholder="Your full name"
              autoComplete="name"
              leftIcon={<User size={18} />}
              error={errors.fullName?.message}
              required
              {...register('fullName')}
            />

            <FormInput
              label="Email"
              type="email"
              placeholder="you@company.com"
              autoComplete="email"
              leftIcon={<Mail size={18} />}
              error={errors.email?.message}
              required
              {...register('email')}
            />

            <FormInput
              label="Phone Number"
              type="tel"
              placeholder="+92 300 1234567"
              autoComplete="tel"
              leftIcon={<Phone size={18} />}
              hint="Include your country code"
              error={errors.phone?.message}
              required
              {...register('phone')}
            />

            <FormInput
              label="Company"
              placeholder="Your company name (optional)"
              autoComplete="organization"
              leftIcon={<Building size={18} />}
              error={errors.company?.message}
              {...register('company')}
            />

            {/* Honeypot — hidden from humans, bots auto-fill it */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
              <input type="text" tabIndex={-1} autoComplete="off" {...register('website')} />
            </div>

            {formError && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30"
                role="alert"
              >
                <AlertCircle size={20} className="text-red-400 shrink-0" />
                <p className="text-sm text-red-400">{formError}</p>
              </motion.div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={isSubmitting}
              leftIcon={isDownload ? <Download size={18} /> : <ExternalLink size={18} />}
            >
              {isDownload ? 'Download PDF' : 'Read Online'}
            </Button>

            <p className="text-xs text-(--color-muted-gray) text-center">
              By continuing you agree to our{' '}
              <Link to={ROUTES.PRIVACY} className="text-(--color-signal-orange) hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        )}
      </ModalBody>
    </Modal>
  );
};

export { WhitepaperAccessModal };
